/**
 * Inline SVG icons for the app shell and the home screen.
 *
 * Inline rather than an icon package: the set is tiny, and every kilobyte of
 * dependency shows up on a low-end Android handset with roughly 2 GB of RAM.
 * They live in the web app rather than in `@sciolingo/renderer` because app
 * chrome is cleanly separated from the task renderer.
 *
 * All of them inherit `currentColor` and size from `1em`, so they follow the
 * surrounding text in both themes.
 */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  )
}

/** Teorie a procvičení */
export function BookIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H10a2 2 0 0 1 2 2v14a1.75 1.75 0 0 0-1.75-1.75H5.5A1.5 1.5 0 0 1 4 15.75Z" />
      <path d="M20 4.5A1.5 1.5 0 0 0 18.5 3H14a2 2 0 0 0-2 2v14a1.75 1.75 0 0 1 1.75-1.75h4.75A1.5 1.5 0 0 0 20 15.75Z" />
    </Icon>
  )
}

/** Cílený trénink */
export function TargetIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/** Test nanečisto */
export function BookmarkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3.75h12a.75.75 0 0 1 .75.75v15.4a.6.6 0 0 1-.94.5L12 16.3l-5.81 4.1a.6.6 0 0 1-.94-.5V4.5A.75.75 0 0 1 6 3.75Z" />
    </Icon>
  )
}

/** Výsledky a slabiny */
export function ChartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 20V11" />
      <path d="M12 20V4" />
      <path d="M19 20v-6" />
    </Icon>
  )
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 12h15" />
      <path d="m13.5 6 6 6-6 6" />
    </Icon>
  )
}

/** The app mark: a rounded blue tile with a four-square glyph. */
export function AppMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 32 32" width="1em" height="1em" aria-hidden="true" focusable="false" {...props}>
      <rect width="32" height="32" rx="8" fill="currentColor" />
      <g fill="#fff">
        <rect x="9" y="9" width="5.5" height="5.5" rx="1.4" />
        <rect x="17.5" y="9" width="5.5" height="5.5" rx="1.4" />
        <rect x="9" y="17.5" width="5.5" height="5.5" rx="1.4" />
        <rect x="17.5" y="17.5" width="5.5" height="5.5" rx="1.4" />
      </g>
    </svg>
  )
}
